import * as Sentry from '@sentry/react'
import React from 'react'
import ReactDOM from 'react-dom/client'

import App from '@/App'
import '@/index.css'
import { PURCHASE_PLUS_GRAPHQL_API, PURCHASE_PLUS_PUBLIC_GRAPHQL_API } from '@/modules/shared/constants'
import { Env } from '@/modules/shared/types'
import reportWebVitals from '@/reportWebVitals'

window.addEventListener('error', (event) => {
  // The chunks all have UUIDs in them and are different each time to avoid caching old versions of the app.
  // So when we redeploy while users are still using the app, this error will be thrown when users navigate between pages because it couldn't fetch the chunks.
  // The solution to this is to forcefully reload the user's session when this error is thrown.
  // In other words, whenever this error occurs, it means there is a new deployment of the app.

  const regexes = [
    /Failed to fetch dynamically imported module/,
    /Failed to load module script/,
    /Unable to preload CSS/,
    /'text\/html' is not a valid JavaScript MIME type\./,
    /error loading dynamically imported module/,
    /Importing a module script failed/,
  ]

  if (event.message && regexes.some((regex) => regex.test(event.message))) {
    window.location.reload()
  }
})

Sentry.init({
  release: 'app-purchase-plus@' + import.meta.env.VITE_APP_VERSION,
  environment: import.meta.env.VITE_APP_BUILD_ENV,
  dsn: import.meta.env.VITE_APP_DSN_VAR,
  integrations: [
    Sentry.breadcrumbsIntegration({
      console: true,
      dom: true,
      fetch: true,
      history: true,
      sentry: true,
      xhr: false,
    }),
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: true,
      maskAllInputs: false,

      // config to show request and reponse on prd
      networkDetailAllowUrls: [PURCHASE_PLUS_GRAPHQL_API, PURCHASE_PLUS_PUBLIC_GRAPHQL_API],
    }),
    Sentry.feedbackIntegration({
      buttonId: 'sentry-feedback-button',
    }),
  ],
  tracesSampleRate: import.meta.env.VITE_APP_BUILD_ENV === Env.Production ? 0.5 : 1.0,

  // setup session replay rates
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  ignoreErrors: [
    // Since, unauthorized error is already handled the dialog, we can ignore it.
    /Unauthorized/,

    /\(Status code 409\)|Received status code 409/, // Conflict error, rage clicks
    /\(Status code 401\)|Received status code 401/, // Unauthorized error
    /\(Status code 403\)|Received status code 403/, // Forbidden errors

    /AbortError: The user aborted a request/,
    /AbortError: signal is aborted without reason/,

    // Handle vite deployment errors (line 15)
    /Failed to fetch dynamically imported module/,
    /Failed to load module script/,
    /Unable to preload CSS/,
    /'text\/html' is not a valid JavaScript MIME type\./,
    /error loading dynamically imported module/,
    /Importing a module script failed/,

    // Loss of internet connection
    /\(Status code undefined\)/,
    /Cannot read properties of undefined \(reading 'data'\)/,
    /undefined is not an object \(evaluating 'e\.data'\)/,
    /UnhandledRejection: Non-Error promise rejection captured with value: Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/,

    /Failed to read a named property 'Element' from 'Window': Blocked a frame with origin/,

    // ignore from third party libraries
    'ResizeObserver loop limit exceeded',
    "Can't find variable: msDiscoverChatAvailable",
    'UnhandledRejection: Non-Error promise rejection captured with value: undefined',
    'E.postMessage is not a function',
    // ignore Safari extensions
    /webkit-masked-url/,

    // ignore password manager induced unhandledRejections https://github.com/getsentry/sentry-javascript/issues/3440
    /UnhandledRejection: Non-Error promise rejection captured with value: Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/,
  ],
  denyUrls: [
    // Regex pattern to match 'preview-purchaseplus.com' in URLs will be denied.
    /preview-purchaseplus\.com/,

    // ignore Chrome extensions, ref: https://docs.sentry.io/platforms/javascript/configuration/filtering/#decluttering-sentry
    /extensions\//i,
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i,

    // ignore google trackers
    /googlesyndication\.com/i,
  ],
})

document.addEventListener('securitypolicyviolation', (event) => {
  Sentry.captureEvent({
    message: `CSP Violation: ${event.violatedDirective}`,
    level: 'warning',
    tags: {
      'csp.violated_directive': event.violatedDirective,
      'csp.effective_directive': event.effectiveDirective,
      'csp.disposition': event.disposition,
    },
    extra: {
      blocked_uri: event.blockedURI,
      document_uri: event.documentURI,
      original_policy: event.originalPolicy,
      source_file: event.sourceFile,
      line_number: event.lineNumber,
      column_number: event.columnNumber,
      status_code: event.statusCode,
    },
  })
})

// Hide the Sentry feedback button with CSS
const style = document.createElement('style')
style.textContent = `
  /* Hide all potential Sentry feedback elements */
  [id*="sentry"],
  [class*="sentry"],
  [data-sentry-component],
  [data-component="feedbackButton"],
  .sentry-feedback-button-wrapper,
  #sentry-feedback-button,
  sentry-feedback-dialog,
  sentry-feedback-widget {
    visibility: hidden !important;
    display: none !important;
    opacity: 0 !important;
    height: 0 !important;
    width: 0 !important;
    position: absolute !important;
    left: -9999px !important;
    top: -9999px !important;
    z-index: -9999 !important;
    overflow: hidden !important;
    clip: rect(0, 0, 0, 0) !important;
    pointer-events: none !important;
  }
`
document.head.appendChild(style)

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
)

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
